import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { EmailTypeEnum, ReminderDirectionEnum, ReminderLineOutDTO, ReminderSettingsOutDTO, ReminderUnitEnum, TransportationTypeEnum } from '@api/ringil-api.v2';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { isNullOrUndefined } from '@utils/utils';
import SvgClock from '@icons/Clock.svg?react';

interface ComponentProps {
  companyId: string;
}

// English fallbacks for the "X hours before <reference>" part of every line; real texts live in Locize
const REFERENCE_DEFAULTS: Partial<Record<EmailTypeEnum, string>> = {
  [EmailTypeEnum.NOT_ASSIGNED_CAR_PLATE_V2]: 'timeslot start',
  [EmailTypeEnum.UNCONFIRMED_LOADING_V2]: 'timeslot start',
  [EmailTypeEnum.UNCONFIRMED_UNLOADING_V2]: 'timeslot start',
  [EmailTypeEnum.UNCONFIRMED_BOTH_V2]: 'timeslot start',
  [EmailTypeEnum.UNCONFIRMED_NO_ACTION_V2]: 'timeslot start',
  [EmailTypeEnum.EXPIRED_DEMAND_V2]: 'demand end',
  [EmailTypeEnum.CA_NO_CMR_INVOICE]: 'last timeslot end',
  [EmailTypeEnum.CA_EXPIRED_GROUP_DEMAND]: 'group demand end',
  [EmailTypeEnum.APPROACHING_END_OF_GD]: 'group demand end',
  [EmailTypeEnum.CU_CA_RESERVATION_NO_ORDERS_REMINDER]: 'reservation start',
  [EmailTypeEnum.NO_ORDER_RESERVATION_ARCHIVED]: 'reservation start',
  [EmailTypeEnum.CU_CA_RESERVATION_NO_SHOW]: 'reservation end',
};

/**
 * Admin-only per-company configuration of scheduled reminder e-mails: when (hours/days before/after the reference
 * moment) and for which modes of transport each e-mail is sent. Inactive config = today's hardcoded defaults.
 */
const ReminderSettingsSection: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { getReminderModuleSettings, upsertReminderModuleSettings, lovTransportationTypes } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [settings, setSettings] = useState<ReminderSettingsOutDTO>();
  const [transportOptions, setTransportOptions] = useState<{ value: TransportationTypeEnum; label: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const load = () => getReminderModuleSettings(companyId, d => setSettings(d));

  useEffect(() => {
    load();
    lovTransportationTypes(d => setTransportOptions(d.map(t => ({ value: t.transportationType, label: t.name }))));
  }, [companyId]);

  if (isNullOrUndefined(settings)) return <></>;

  const patchLine = (emailType: EmailTypeEnum, patch: Partial<ReminderLineOutDTO>) =>
    setSettings(s => ({ ...s, lines: s.lines.map(l => (l.emailType === emailType ? { ...l, ...patch } : l)) }));

  const setValue = (line: ReminderLineOutDTO, idx: number, value: number | null) =>
    patchLine(line.emailType, { values: line.values.map((v, i) => (i === idx ? (value ?? line.defaults[idx]) : v)) });

  const unitLabel = (unit: ReminderUnitEnum) =>
    unit === ReminderUnitEnum.DAYS ? tr('reminderSettings.unit.DAYS', 'days') : tr('reminderSettings.unit.HOURS', 'hours');
  const directionLabel = (direction: ReminderDirectionEnum) =>
    direction === ReminderDirectionEnum.BEFORE ? tr('reminderSettings.direction.BEFORE', 'before') : tr('reminderSettings.direction.AFTER', 'after');
  const referenceLabel = (emailType: EmailTypeEnum) => tr(`reminderSettings.reference.${emailType}`, REFERENCE_DEFAULTS[emailType] ?? emailType);

  const save = () => {
    setSaving(true);
    upsertReminderModuleSettings(
      companyId,
      {
        active: settings.active,
        lines: settings.lines.map(l => ({ emailType: l.emailType, enabled: l.enabled, values: l.values, transportationType: l.transportationType })),
      },
      () => {
        setSaving(false);
        showToastMessage(tr('generic.saved', 'Saved'), tr('reminderSettings.saved', 'Reminder settings saved'), 'success');
        load();
      },
      undefined,
      {
        onError: (_error, showStandardError) => {
          setSaving(false);
          showStandardError();
        },
      },
    );
  };

  return (
    <SectionPanel
      icon={<SvgClock className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('adminCompanyDetail.sectionReminders', 'Reminders')}
      dataTestId={'admin-company-section-reminders'}>
      <div className={'flex flex-col gap-4'}>
        <RingilInputSwitch
          value={settings.active}
          onChange={v => setSettings(s => ({ ...s, active: v }))}
          label={tr('reminderSettings.active', 'Use custom reminder settings (otherwise defaults apply)')}
          dataTestId={'admin-company-reminders-active'}
        />
        {!settings.active && (
          <span className={'text-content-secondary'}>
            {tr('reminderSettings.inactiveHint', 'Values below can be prepared and saved now; the scheduler starts using them once the settings are activated.')}
          </span>
        )}

        <div className={'flex flex-col divide-y divide-content-quaternary'}>
          {settings.lines.map(line => (
            <div key={line.emailType} className={'grid grid-cols-[2rem_18rem_1fr_16rem] items-center gap-4 py-2'} data-testid={`reminder-line-${line.emailType}`}>
              <InputSwitch checked={line.enabled} onChange={e => patchLine(line.emailType, { enabled: e.value })} />
              <div className={'flex flex-col'}>
                <span className={!settings.active || !line.enabled ? 'text-content-tertiary' : ''}>{line.title}</span>
                <span className={'text-xs text-content-tertiary'}>{line.emailType}</span>
              </div>
              <div className={'flex flex-row flex-wrap items-center gap-2'}>
                {line.values.map((value, idx) => (
                  <div key={idx} className={'flex flex-row items-center gap-2'}>
                    {line.values.length > 1 && (
                      <span className={'text-content-secondary'}>
                        {idx === 0 ? tr('reminderSettings.first', '1st') : tr('reminderSettings.second', '2nd')}
                      </span>
                    )}
                    <InputNumber
                      value={value}
                      min={line.min}
                      max={line.max}
                      showButtons
                      useGrouping={false}
                      inputClassName={'w-20'}
                      onChange={e => setValue(line, idx, e.value)}
                    />
                    <span className={'text-content-secondary whitespace-nowrap'}>
                      {unitLabel(line.reminderUnit)} {directionLabel(line.reminderDirection)} {referenceLabel(line.emailType)}
                    </span>
                  </div>
                ))}
                <span className={'text-xs text-content-tertiary whitespace-nowrap'}>
                  ({line.min}–{line.max}, {tr('reminderSettings.default', 'default')} {line.defaults.join(' / ')})
                </span>
              </div>
              <div>
                {line.expeditionBased ? (
                  <MultiSelect
                    options={transportOptions}
                    optionLabel={'label'}
                    optionValue={'value'}
                    value={line.transportationType}
                    placeholder={tr('reminderSettings.allTransportationTypes', 'All modes of transport')}
                    onChange={e => patchLine(line.emailType, { transportationType: e.value })}
                    className={'w-full'}
                  />
                ) : (
                  <span className={'text-xs text-content-tertiary'}>{tr('reminderSettings.transportNotApplicable', 'Mode of transport not applicable')}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div>
          <RingilButton mode={'filled'} label={tr('generic.save', 'Save')} onClick={() => save()} disabled={saving} dataTestId={'admin-company-reminders-save'} />
        </div>
      </div>
    </SectionPanel>
  );
};

export default ReminderSettingsSection;
